import { useState, useEffect } from "react";

import TagsOverflow from "../tags/TagsOverflow";
import ComboBox from "../core/ComboBox";

import useTriggeredAPICall from "../../hooks/fetch-hooks/useTriggeredAPICall";
import { useAuthInfo } from "../../context/AuthContext";
import fetchWrapper from "../../lib/apiCall";

import { randomColor } from "../../util/ColorUtil";
import { truncateName } from "../../util/stringUtils";
import { errorToast } from "../../util/toastNotifications";

const NAME_MAX_LENGTH = 30;
const TAGS_PER_PAGE = 100;

const resolveTagId = (tag) => tag?.tag_id ?? tag?.id ?? tag?.tag?.tag_id ?? tag?.tag?.id;

const dedupeSpecialTags = (tags) => {
  const seenSpecial = new Set();
  return tags.filter((tag) => {
    const name = (tag.name || "").toLowerCase();
    if (name === "blocked" || name === "blocking") {
      if (seenSpecial.has(name)) return false;
      seenSpecial.add(name);
    }
    return true;
  });
};

const fetchAllOrgTags = async (orgId) => {
  const allTags = [];
  let page = 1;
  let totalPages = 1;

  while (page <= totalPages) {
    const response = await fetchWrapper.apiCall(
      `/tags?org_id=${orgId}&page=${page}&per_page=${TAGS_PER_PAGE}`,
      "GET"
    );
    allTags.push(...(response?.results || []));
    totalPages = response?.pagination?.pages || 1;
    page += 1;
  }

  return dedupeSpecialTags(allTags);
};

const FormFieldTags = ({
  selectedTags,
  handleTagRemove,
  handleAddTagToObject,
  errorMessage,
  objectType,
  objectId,
  objectPk,
  orgId,
  hideLabel = false,
  ...props
}) => {
  const { userInfo } = useAuthInfo();
  const { fetchData } = useTriggeredAPICall();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [allTags, setAllTags] = useState([]);
  const [inactiveTags, setInactiveTags] = useState([]);

  useEffect(() => {
    const effectiveOrgId = orgId || userInfo?.org_id;
    if (!isModalOpen || !effectiveOrgId) {
      return;
    }

    let cancelled = false;

    fetchAllOrgTags(effectiveOrgId)
      .then((tags) => {
        if (!cancelled) {
          setAllTags(tags.filter((t) => t.is_active !== false && t.is_active !== 0));
          setInactiveTags(tags.filter((t) => t.is_active === false || t.is_active === 0));
        }
      })
      .catch((err) => console.error("Fetch Tags", err));

    return () => {
      cancelled = true;
    };
  }, [isModalOpen, orgId, userInfo?.org_id]);

  const handleOpenAddTag = (e) => {
    setIsModalOpen(true);
  };

  const formatDataForDisplay = (tag) => truncateName(tag.name);

  const handleTagSelectFromDropdown = (tagId) => {
    const isAlreadySelected = selectedTags?.some(
      (t) => String(resolveTagId(t)) === String(tagId)
    );
    if (isAlreadySelected) {
      handleTagRemove?.(tagId);
    } else {
      handleAddTagToObject?.(tagId);
    }
  };

  const createNewTag = (tagName) => {
    if (tagName.trim().length === 0) return;

    const trimmedLower = tagName.trim().toLowerCase();
    const isInactive = inactiveTags.some(
      (t) => (t.name || "").toLowerCase().trim() === trimmedLower
    );
    if (isInactive) {
      errorToast("Cannot create duplicate tag");
      return;
    }

    const color = randomColor();
    const body = { name: tagName, color };

    fetchData("/tag", "POST", body)
      .then((data) => {
        handleAddTagToObject(data.results.tag_id);
        setAllTags((prevTags) => [...prevTags, data.results]);
      })
      .catch((err) => console.error("Add Tag", err));
  };

  return (
    <div className="input-container flex-columns">
      {handleAddTagToObject && !hideLabel && (
        <label htmlFor="form-field-tags">Tags</label>
      )}

      <div className="input-tag-container">
        <TagsOverflow
          selectedTags={selectedTags}
          handleTagRemove={handleTagRemove}
        />
        {isModalOpen ? (
          <ComboBox
            data={allTags}
            selected={selectedTags}
            valueKey="tag_id"
            placeholder="Search or add tag..."
            searchTopic="tag"
            formatData={formatDataForDisplay}
            handleSelect={handleTagSelectFromDropdown}
            handleNewItem={createNewTag}
            onRequestClose={() => setIsModalOpen(false)}
            maxLength={NAME_MAX_LENGTH}
            createNewMatchKey="name"
            sortKey="name"
            allowMultiple={true}
            extraMatchData={inactiveTags}
          />
        ) : (
          handleAddTagToObject != null && (
            <div
              className="tag add-tag"
              key="placeholder"
              onClick={handleOpenAddTag}
            >
              + Add Tag
            </div>
          )
        )}
      </div>
      <div className="error-msg">{errorMessage}</div>
    </div>
  );
};

export default FormFieldTags;
